import {
  index as indexUserSettingLayout,
  update as updateUserSettingLayout
} from "~/api/userSettingLayout";
import * as types from "../mutation-types";

function isLayoutPayloadCandidate(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return (
    Array.isArray(value.data) ||
    (value.data && typeof value.data === "object") ||
    Array.isArray(value.items) ||
    (value.items && typeof value.items === "object") ||
    value.data === null ||
    value.schemaVersion !== undefined ||
    value.layoutsByViewport !== undefined ||
    value.typeId !== undefined ||
    value.updatedAt !== undefined
  );
}

function resolveLayoutPayload(payload = null) {
  const candidates = [payload?.data, payload?.data?.data, payload];

  for (const candidate of candidates) {
    if (candidate === null) {
      return null;
    }

    if (Array.isArray(candidate)) {
      return { data: candidate };
    }

    if (isLayoutPayloadCandidate(candidate)) {
      return candidate;
    }
  }

  return null;
}

function toNumericTypeId(typeId) {
  const numericTypeId = Number(typeId);

  if (!Number.isFinite(numericTypeId)) {
    return null;
  }

  return numericTypeId;
}

export const state = {
  itemsByTypeId: {},
  isLoading: false,
  updatedAt: null
};

export const mutations = {
  [types.SET_IS_LOADING_USER_SETTING_LAYOUT](state, status) {
    state.isLoading = Boolean(status);
  },
  [types.SET_USER_SETTING_LAYOUT](state, payload) {
    const numericTypeId = toNumericTypeId(payload?.typeId);

    if (numericTypeId === null) {
      return;
    }

    state.itemsByTypeId = {
      ...state.itemsByTypeId,
      [String(numericTypeId)]: payload?.setting ?? null
    };
  },
  [types.SET_USER_SETTING_LAYOUT_UPDATED_AT](state, timestamp) {
    state.updatedAt = timestamp;
  }
};

export const actions = {
  async fetchByType({ commit }, typeId) {
    const numericTypeId = toNumericTypeId(typeId);

    if (numericTypeId === null) {
      return null;
    }

    commit(types.SET_IS_LOADING_USER_SETTING_LAYOUT, true);

    try {
      const response = await indexUserSettingLayout(numericTypeId);
      const setting = resolveLayoutPayload(response?.data);

      commit(types.SET_USER_SETTING_LAYOUT, {
        typeId: numericTypeId,
        setting
      });
      commit(types.SET_USER_SETTING_LAYOUT_UPDATED_AT, Date.now());

      return setting;
    } finally {
      commit(types.SET_IS_LOADING_USER_SETTING_LAYOUT, false);
    }
  },

  async updateByType({ commit }, payload = {}) {
    const numericTypeId = toNumericTypeId(payload?.typeId);

    if (numericTypeId === null) {
      return null;
    }

    const request = {
      data: Array.isArray(payload?.data) || (
        payload?.data &&
        typeof payload.data === "object"
      )
        ? payload.data
        : []
    };

    commit(types.SET_IS_LOADING_USER_SETTING_LAYOUT, true);

    try {
      const response = await updateUserSettingLayout(numericTypeId, request);
      const setting = resolveLayoutPayload(response?.data) || {
        typeId: numericTypeId,
        data: request.data
      };

      commit(types.SET_USER_SETTING_LAYOUT, {
        typeId: numericTypeId,
        setting
      });
      commit(types.SET_USER_SETTING_LAYOUT_UPDATED_AT, Date.now());

      return setting;
    } finally {
      commit(types.SET_IS_LOADING_USER_SETTING_LAYOUT, false);
    }
  }
};

export const getters = {
  getByType: (state) => (typeId) => {
    const numericTypeId = toNumericTypeId(typeId);

    if (numericTypeId === null) {
      return null;
    }

    return state.itemsByTypeId[String(numericTypeId)] ?? null;
  },
  isLoading: (state) => state.isLoading,
  updatedAt: (state) => state.updatedAt
};
